import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import IAlert from "../../types/Alert";
import ConfigUtils from "../../utils/Config";
import { Alert, Autocomplete, Backdrop, Box, Button, Checkbox, Chip, CircularProgress, Collapse, FormControl, IconButton, InputLabel, LinearProgress, MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import * as constantes from "../../common/Constantes";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import FacturarCitaDialog from "./facturarCita/FacturarCitaDialog";
import TablePagination from "../utilities/TablePagination";
import FacturacionService from "../../services/FacturacionService";
import { aplicarAnulacion, aplicarCambioEntidad } from "./common/filasAnulacion";
import { dentroDelPeriodo, fechaExpedicionFila, fechaListado, useFilasListado } from "./common/fechasListado";
import EstadoFacturacionIcon, { EstadoFacturacion, EstadoFacturacionToColorUnified, getEstadoFacturacion, etiquetaEstadoFacturacion } from "./common/EstadoFacturacionIcon";
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from "@mui/icons-material/Download";
import DetalleFacturaComplementaria from "./facturarCita/DetalleFacturaComplementaria";
import IPaciente from "../../types/Paciente";
import PacienteService from "../../services/PacienteService";
import DetalleFacturaBono from "./facturarBono/DetalleFacturaBono";
import DetalleFacturaClase from "./facturarClase/DetalleFacturaClase";
import CrearFacturaManualForm from "./common/CrearFacturaManualForm";
import DetalleFacturaManual from "./common/DetalleFacturaManual";
import { formatCurrency, formatNumeroFactura, getFechaExpedicion } from "../../utils/Factura";
import SelectorEntidadConsulta, { getEntidadesConsulta, slugifyEntidadNombre } from "./common/SelectorEntidadConsulta";

const locale = constantes.locale;

export interface IFactura {
  _id: string;
  numero?: string;
  fechaFactura: string | null;
  fechaExpedicion?: string | null; // la del PDF; el filtro de fechas del listado va por ella
  tipo: string;
  modalidad: string;
  servicio: string;
  pacienteId: string | null;
  paciente: string | null;
  servicioId: string;
  total: number;
  estado: EstadoFacturacion;
  facturaRelacionada: IFactura | undefined;
  fechaPago?: string | null;
  motivoAnulacion?: string;
  moneda?: string;
  entityId?: string | null;
  huerfana?: boolean;
}

const Facturas = () => {
    const history = useNavigate();
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
    // Una fila que se acaba de emitir toma la fecha de expedición de hoy (ver useFilasListado).
    const [registros, setRegistros] = useFilasListado();
    const [pacientes, setPacientes] = useState<IPaciente[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalFacturarCita, setModalFacturarCita] = useState(false);
    const [selectedServicio, setSelectedServicio] = useState<IFactura | null>(null);

    const [filterStart, setFilterStart] = useState<Date | null>(null);
    const [filterEnd, setFilterEnd] = useState<Date | null>(null);
    const [filterPaciente, setFilterPaciente] = useState<string | null>(null);
    const [filterTipo, setFilterTipo] = useState("");
    const [filterModalidad, setFilterModalidad] = useState("");
    const [filterEstado, setFilterEstado] = useState("");
    const [filterServicios, setFilterServicios] = useState<string[]>([]);
    const [filterEntidad, setFilterEntidad] = useState<string | null>(null); // null = principal, por defecto

    const [page, setPage] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [modalDetalleComplementaria, setModalDetalleComplementaria] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    // Descarga del lote: facturas pedidas y % recibido (null mientras el servidor prepara los datos y el primer PDF).
    const [facturasEnDescarga, setFacturasEnDescarga] = useState(0);
    const [progresoDescarga, setProgresoDescarga] = useState<number | null>(null);

    const [modalDetalleFacturaBono, setModalDetalleFacturaBono] = useState(false);
    const [facturaId, setFacturaId] = useState("");
    const [modalDetalleFacturaClase, setModalDetalleFacturaClase] = useState(false);

    const [modalCrearFacturaManual, setModalCrearFacturaManual] = useState(false);
    const [modalDetalleFacturaManual, setModalDetalleFacturaManual] = useState(false);
    
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
      load();
    }, []);
    
    useEffect(() => {
      setPage(0);
    }, [filterStart, filterEnd, filterPaciente, filterTipo, filterModalidad, filterEstado, filterServicios, filterEntidad]);

    const userId = ConfigUtils.getUserId();

    const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

    const load = async () => {
      try {
        const [pacientesResp, facturasParcialesResp] = await Promise.all([
          PacienteService.getAll(),
          FacturacionService.getFacturas(userId),
        ]);
        setPacientes(pacientesResp.data || []);
        setRegistros(facturasParcialesResp.data || []);
      }  catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "Facturas.Component.getFacturas",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoading(false);
      }
    };

    if (loading) return <CircularProgress />;

    const filteredRegistros = registros.filter(r => {
      // Por fecha de expedición (la de emisión, o la de creación si no está emitida); la de pago es solo una columna.
      if (!dentroDelPeriodo(r, filterStart, filterEnd)) return false;
      
      if (filterPaciente && (!r.paciente || r.paciente.toLowerCase().indexOf(filterPaciente.toLowerCase()) === -1)) return false;
      if (filterTipo && filterTipo !== "" && r.tipo !== filterTipo.toLowerCase()) return false;
      if (filterModalidad && filterModalidad !== "" && r.modalidad !== filterModalidad.toLocaleLowerCase()) return false;
      if (filterEstado && filterEstado !== "" && r.estado !== filterEstado.toLowerCase()) return false;
      if (filterServicios && Array.isArray(filterServicios) && filterServicios.length > 0) {
        if (!r.servicio || !filterServicios.includes(r.servicio)) return false;
      }
      if ((r.entityId ?? null) !== filterEntidad) return false;
      return true;
    });

    const pacientesUnicos = Array.from(
      new Set(registros.map(r => r.paciente).filter(p => p !== null))
    ).sort((a, b) => a!.localeCompare(b!, "es", { sensitivity: "base" }));

    const tipos = Array.from(
      new Set(registros.map(r => r.tipo).filter(p => p !== null))
    ).map(p => p.charAt(0).toUpperCase() + p.slice(1));

    const estados = Array.from(
      new Set(registros.map(r => r.estado).filter(p => p !== null))
    ).map(p => p.charAt(0).toUpperCase() + p.slice(1));

    const modalidades = Array.from(
      new Set(registros.map(r => r.modalidad).filter(p => p !== null))
    ).map(p => p.charAt(0).toUpperCase() + p.slice(1));

    const servicios = Array.from(
      new Set(registros.map(r => r.servicio).filter(p => p !== null))
    ).sort((a, b) => a!.localeCompare(b!, "es", { sensitivity: "base" }));

    const registrosPaginados = filteredRegistros.slice(
      page * constantes.rowsPerPage,
      page * constantes.rowsPerPage + constantes.rowsPerPage
    );

    const parseEstadoFacturacion = (estadoKey?: string): EstadoFacturacion =>
        Object.values(EstadoFacturacion).includes(estadoKey as EstadoFacturacion)
          ? (estadoKey as EstadoFacturacion)
          : EstadoFacturacion.SIN_FACTURA;

    const inputStyle = {
      "& .MuiInputBase-root": {
        height: 40,
        minHeight: 40,
        fontSize: 13,
        "& .MuiInputBase-input": {
          padding: "8px 12px",
          height: 24,
          boxSizing: "border-box",
          fontSize: 13,
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: 13,
      },
      "& .MuiAutocomplete-inputRoot": {
        minHeight: 40,
      },
    };

    const handleDownloadBatch = async () => {
      try {

        if (selectedIds.length > 1000) {
          setAlert({ open: true, severity: "warning", msg: "Máximo 1.000 facturas por descarga" });
          return;
        }

        setFacturasEnDescarga(selectedIds.length);
        setProgresoDescarga(null);
        setIsDownloading(true);
        // El servidor envía en una cabecera el tamaño estimado del ZIP (no se conoce el real hasta el final): el % es lo
        // recibido sobre esa estimación, como mucho 99 hasta que termina.
        let bytesEstimados = 0;
        const response = await FacturacionService.descargarFacturas({
          userId: ConfigUtils.getUserId(),
          facturaIds: selectedIds,
        }, (evento) => {
          const xhr: unknown = evento.event?.target;
          if (!bytesEstimados && xhr instanceof XMLHttpRequest) bytesEstimados = Number(xhr.getResponseHeader("X-Bytes-Estimados")) || 0;
          if (bytesEstimados > 0) setProgresoDescarga(Math.min(99, Math.floor((evento.loaded / bytesEstimados) * 100)));
        });
      
        const blob = new Blob([response.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const year = today.getFullYear();

        const entidadesConsulta = getEntidadesConsulta();
        const nombreEntidad = entidadesConsulta.length > 1
          ? entidadesConsulta.find(ent => ent.entidadId === filterEntidad)?.nombre
          : undefined;
        const slugEntidad = nombreEntidad ? `-${slugifyEntidadNombre(nombreEntidad)}` : "";

        const a = document.createElement("a");
        a.href = url;
        a.download = `facturas${slugEntidad}-${day}-${month}-${year}.zip`;
        a.click();
      
        window.URL.revokeObjectURL(url);
        setSelectedIds([]);
        setAlert({ open: true, severity: "success", msg: "Lote descargado correctamente" });
      } catch (e: unknown) {
        // Sin respuesta: la conexión se cortó a medias (red, o el servidor la cortó por un error con el ZIP ya en marcha).
        const interrumpida = isAxiosError(e) && !e.response;
        setAlert({
          open: true,
          severity: "error",
          msg: interrumpida ? "La descarga se ha interrumpido. Vuelve a intentarlo." : "No se han podido descargar las facturas",
        });
      } finally {
        setIsDownloading(false);
      }
    };

    const selectedSet = new Set(selectedIds);

    const allFilteredIds = filteredRegistros.map(r => r._id);
    
    const allSelected =
      allFilteredIds.length > 0 &&
      allFilteredIds.every(id => selectedSet.has(id));
    
    const toggleSelectAll = () => {
      setSelectedIds(prev => {
        if (allSelected) return [];
        return Array.from(new Set([...prev, ...allFilteredIds]));
      });
    };

    const toggleOne = (id: string, checked: boolean) => {
      setSelectedIds(prev => {
        if (checked) return Array.from(new Set([...prev, id]));
        return prev.filter(x => x !== id);
      });
    };

    return(
        <>        
          {registros.length === 0 ? (
            <Typography variant="body1">
              No hay facturas.
            </Typography>
          ) : (
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
                mb={3}
                gap={2}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      flex: 1,
                      minWidth: 0,
                      fontSize: { xs: "1.25rem", sm: "1.75rem" },
                      whiteSpace: { xs: "nowrap", sm: "normal" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Facturas
                  </Typography>
                  
                  {!isDesktop && (
                    <IconButton onClick={() => setShowFilters(prev => !prev)}>
                      <FilterListIcon />
                    </IconButton>
                  )}
                </Box>
                
                <Button
                  variant="contained"
                  onClick={() => setModalCrearFacturaManual(true)}
                  fullWidth={!isDesktop}
                  sx={{
                    alignSelf: { xs: "stretch", sm: "auto" },
                    whiteSpace: "nowrap",
                    px: 4,
                  }}
                >
                  Nueva factura
                </Button>
              </Box>
            
              {/* FILTROS */}
              <Collapse in={isDesktop || showFilters} timeout="auto" unmountOnExit>
              <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                  <SelectorEntidadConsulta value={filterEntidad} onChange={value => { setFilterEntidad(value); setSelectedIds([]); }} />
                  <DatePicker
                    label="Desde"
                    value={filterStart}
                    onChange={setFilterStart}
                    renderInput={(params) => (
                      <TextField {...params} sx={{ width: { xs: "100%", sm: 140 }, ...inputStyle }} size="small" InputLabelProps={{ style: { fontSize: 13 } }} />
                    )}
                  />
                  <DatePicker
                    label="Hasta"
                    value={filterEnd}
                    onChange={setFilterEnd}
                    renderInput={(params) => (
                      <TextField {...params} sx={{ width: { xs: "100%", sm: 140 }, ...inputStyle }} size="small" InputLabelProps={{ style: { fontSize: 13 } }} />
                    )}
                  />
                  <Autocomplete
                    disablePortal
                    options={pacientesUnicos}
                    value={filterPaciente}
                    onChange={(event, newValue) => setFilterPaciente(newValue)}
                    renderInput={(params) => <TextField {...params} label="Paciente" size="small" sx={{ ...inputStyle, minWidth: 80, width: "100%" }} InputLabelProps={{ style: { fontSize: 13 } }} />}
                    sx={{ flexGrow: 1, minWidth: 100 }}
                  />
                  <FormControl sx={{ width: { xs: "100%", sm: 100 } }} size="small">
                    <InputLabel sx={{ fontSize: 13 }}>Tipo</InputLabel>
                    <Select
                      value={filterTipo}
                      onChange={(e) => setFilterTipo(e.target.value)}
                      label="Tipo"
                      sx={{
                        "& .MuiSelect-select": {
                          fontSize: 13,
                        }
                      }}
                    >
                      {tipos.map(t => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ width: { xs: "100%", sm: 120 } }} size="small">
                    <InputLabel sx={{ fontSize: 13 }}>Servicio</InputLabel>
                    <Select
                      multiple
                      value={filterServicios}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilterServicios(typeof value === "string" ? value.split(",") : value);
                      }}
                      label="Servicio"
                      renderValue={(selected) => (selected as string[]).join(", ")}
                      sx={{
                        "& .MuiSelect-select": {
                          fontSize: 13,
                        }
                      }}
                    >
                      {servicios.map(t => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ width: { xs: "100%", sm: 100 } }} size="small">
                    <InputLabel sx={{ fontSize: 13 }}>Modalidad</InputLabel>
                    <Select
                      value={filterModalidad}
                      onChange={(e) => setFilterModalidad(e.target.value)}
                      label="Modadalidad"
                      sx={{
                        "& .MuiSelect-select": {
                          fontSize: 13,
                        }
                      }}
                    >
                      {modalidades.map(t => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ width: { xs: "100%", sm: 120 } }} size="small">
                    <InputLabel sx={{ fontSize: 13 }}>Estado</InputLabel>
                    <Select
                      value={filterEstado}
                      onChange={(e) => setFilterEstado(e.target.value)}
                      label="Estado"
                      sx={{
                        "& .MuiSelect-select": {
                          fontSize: 13,
                        }
                      }}
                    >
                      {estados.map(s => (
                        <MenuItem key={s} value={s}>{etiquetaEstadoFacturacion(s)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                    onClick={() => {
                      setFilterStart(null);
                      setFilterEnd(null);
                      setFilterPaciente(null);
                      setFilterTipo("");
                      setFilterEstado("");
                      setFilterServicios([]);
                      setFilterModalidad("");
                      setFilterEntidad(null);
                      setSelectedIds([]);
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </Box>
              </LocalizationProvider>
              </Collapse>

              {filteredRegistros.length > 0 && (
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "white",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ pl: 1 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={1}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedIds.length > 0 && !allSelected}
                        onChange={toggleSelectAll}
                      />

                      <Typography variant="body2">
                        {selectedIds.length > 0
                          ? `${selectedIds.length} seleccionadas`
                          : "Seleccionar todas"}
                      </Typography>
                    </Box>
                        
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      disabled={selectedIds.length === 0 || isDownloading}
                      onClick={handleDownloadBatch}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        mb: {xs: 2, sm: 0}
                      }}
                    >
                      Descargar facturas
                    </Button>
                  </Box>
                </Box>
              )}

              {isMobile ? 
              <Box>
                {registrosPaginados.map((item) => {
                  const estado = parseEstadoFacturacion(item.estado);
                  const colores = EstadoFacturacionToColorUnified[estado];
                
                  return (
                    <Paper key={item._id} sx={{ p: 2, mb: 2, borderRadius: 2, position: "relative" }}>

                      <Checkbox
                        checked={selectedSet.has(item._id)}
                        onChange={(e) => toggleOne(item._id, e.target.checked)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          padding: 0,
                        }}
                      />
  
                      <Typography variant="body1" fontWeight={500}>
                        {item.paciente}
                      </Typography>

                      <Box mt={0.5}>
                        {item.estado === EstadoFacturacion.ANULADA ? (
                          <Chip
                            label={item.estado}
                            size="small"
                            sx={{ textTransform: "capitalize", border: `1px solid #d32f2f`, color: "#d32f2f" }}
                          />
                        ) : (
                          <Chip
                            label={etiquetaEstadoFacturacion(item.estado)}
                            color={colores.chip}
                            size="small"
                            sx={{ textTransform: "capitalize" }}
                          />
                        )}
                      </Box>

                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {[fechaListado(fechaExpedicionFila(item)), formatNumeroFactura(item.numero)].filter(Boolean).join(" · ")}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {[item.tipo, item.servicio, item.modalidad ?? (item.huerfana ? "NA" : null)]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                        
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={1.5}
                      >
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(item.total, item.moneda)}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" gap={1}>
                          <EstadoFacturacionIcon
                            estado={estado}
                            onClick={() => {
                              setSelectedServicio(item);
                              // Anulada por cambio de entidad: sin vínculo de servicio, se abre con los datos de la propia factura.
                              if (item.huerfana) {
                                setModalDetalleFacturaManual(true);
                                setFacturaId(item._id);
                                return;
                              }
                              if (item.tipo === "cita") setModalFacturarCita(true);
                              if (item.tipo === "agrupada") setModalDetalleComplementaria(true);
                              if (item.tipo === "bono") {
                                setModalDetalleFacturaBono(true);
                                setFacturaId(item._id);
                              }
                              if (item.tipo === "clase") {
                                setModalDetalleFacturaClase(true);
                                setFacturaId(item._id);
                              }
                              if (item.tipo === "manual") {
                                setModalDetalleFacturaManual(true);
                                setFacturaId(item._id);
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box> : 
              <TableContainer>
              <Table size="small">
                <TableHead 
                  sx={{ 
                    backgroundColor: "grey.100",
                    "& .MuiTableCell-head": {
                      fontWeight: 600,
                      color: "grey.800",
                      fontSize: "0.95rem",
                    }
                  }}
                >
                  <TableRow>
                    <TableCell />
                    <TableCell sx={{ width: "20%" }}>Número</TableCell>
                    <TableCell sx={{ width: { xs: 110, sm: 130 }, whiteSpace: "nowrap" }}>Fecha de expedición</TableCell>
                    <TableCell sx={{ width: { xs: 110, sm: 130 }, whiteSpace: "nowrap" }}>Fecha de pago</TableCell>
                    <TableCell sx={{ width: "30%" }}>Paciente</TableCell>
                    <TableCell sx={{ width: "20%" }}>Tipo</TableCell>
                    <TableCell sx={{ width: "20%" }}>Servicio</TableCell>
                    <TableCell sx={{ width: "20%" }}>Modalidad</TableCell>
                    <TableCell sx={{ width: "20%" }}>Estado</TableCell>
                    <TableCell sx={{ width: "20%" }}>Importe</TableCell>
                    <TableCell sx={{ width: 60 }} align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
              
                <TableBody>
                  {registrosPaginados.map((item, index) => {
                    const estado = parseEstadoFacturacion(item.estado);
                    const colores = EstadoFacturacionToColorUnified[estado];
                
                    return (
                      <TableRow 
                        key={item._id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "white" : "grey.50",
                          "&:hover": {
                            backgroundColor: "grey.100"
                          }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedSet.has(item._id)}
                            onChange={(e) => toggleOne(item._id, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>{formatNumeroFactura(item.numero)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{fechaListado(fechaExpedicionFila(item))}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.fechaPago ? new Date(item.fechaPago).toLocaleDateString("es-ES") : ""}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{item.paciente}</TableCell>
                        <TableCell>{item.tipo ? item.tipo.charAt(0).toUpperCase() + item.tipo.slice(1) : ''}</TableCell>
                        <TableCell>{item.servicio}</TableCell>
                        <TableCell>{item.modalidad ? item.modalidad.charAt(0).toUpperCase() + item.modalidad.slice(1) : item.huerfana ? 'NA' : ''}</TableCell>
                        <TableCell>
                          {item.estado === EstadoFacturacion.ANULADA ? (
                            <Chip
                              label={item.estado}
                              size="small"
                              sx={{ textTransform: "capitalize", border: `1px solid #d32f2f`, color: "#d32f2f" }}
                            />
                          ) : (
                            <Chip
                              label={etiquetaEstadoFacturacion(item.estado)}
                              color={colores.chip}
                              size="small"
                              sx={{ textTransform: "capitalize" }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatCurrency(item.total, item.moneda)}</TableCell>
                        <TableCell align="right">
                          <EstadoFacturacionIcon
                            estado={estado}
                            onClick={() => {
                              setSelectedServicio(item);
                              // Anulada por cambio de entidad: sin vínculo de servicio, se abre con los datos de la propia factura.
                              if (item.huerfana) {
                                setModalDetalleFacturaManual(true);
                                setFacturaId(item._id);
                                return;
                              }
                              if (item.tipo === "cita") setModalFacturarCita(true);
                              if (item.tipo === "agrupada") {
                                setModalDetalleComplementaria(true);
                              }
                              if (item.tipo === "bono") {
                                setModalDetalleFacturaBono(true);
                                setFacturaId(item._id);
                              }
                              if (item.tipo === "clase") {
                                setModalDetalleFacturaClase(true);
                                setFacturaId(item._id);
                              }
                              if (item.tipo === "manual") {
                                setModalDetalleFacturaManual(true);
                                setFacturaId(item._id);
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TableContainer>}
              
              {/* PAGINACIÓN */}
              <TablePagination
                count={filteredRegistros.length}
                page={page}
                rowsPerPage={constantes.rowsPerPage}
                onPageChange={setPage}
              />
            </Paper>
          )}
    
        {/* FACTURAR CITA */}
        {modalFacturarCita && 
            <FacturarCitaDialog 
                open={modalFacturarCita} 
                onClose={() => {setModalFacturarCita(false); setSelectedServicio(null)}} 
                tipo="original"
                citaId={selectedServicio?.modalidad === "rectificativa" ? selectedServicio._id : selectedServicio?.servicioId!}
                pacienteId={selectedServicio?.pacienteId!}
                onServicioActualizado={({ _id: servicioId, sinAviso, ...cambios }) => {
                  setRegistros(prev =>
                    prev.map(r => r.servicioId === servicioId ? { ...r, ...cambios } : r)
                  );
                  if (!sinAviso) setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                }}
                onFacturaRectificada={(factura) => {
                  const facturaReducida: IFactura = {
                    _id: factura._id,
                    numero: factura.numero,
                    fechaFactura: factura.fechaFactura.toString(),
                    fechaExpedicion: getFechaExpedicion(factura),
                    tipo: factura.tipo,
                    modalidad: "rectificativa",
                    servicio: factura.servicio,
                    pacienteId: null,
                    paciente: factura.receptor.nombre,
                    servicioId: "",
                    total: factura.importeTotal,
                    estado: getEstadoFacturacion(factura.estado, factura.estadoPago),
                    facturaRelacionada: undefined,
                    fechaPago: factura.metodoPago?.fechaPago.toString(),
                    entityId: factura.entityId ?? null,
                  };
                  setRegistros(prev => [facturaReducida, ...prev]);
                  setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
                }}
                onRectificativaActualizada={(updated) => {
                  setRegistros(prev =>
                    prev.map(r => r._id === updated._id ? { ...r, ...updated } : r)
                  );
                  setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                }}
                onAnularFactura={(id, _motivo, _devolverSesiones, anulacion) => {
                  setRegistros(prev => aplicarAnulacion(prev, id, anulacion));
                }}
                onEntidadCambiada={(facturaAnuladaId, facturaNueva, anulacion) => {
                  setRegistros(prev => aplicarCambioEntidad(prev, facturaAnuladaId, facturaNueva, anulacion));
                  setAlert({ open: true, severity: "success", msg: "Entidad de facturación cambiada correctamente" });
                }}
                onFacturaEliminada={(id) => {
                  setRegistros(prev => prev.filter(r => r._id !== id));
                  setAlert({ open: true, severity: "success", msg: "Factura eliminada correctamente" });
                }}
            />
        }

        {/* DETALLE FACTURA COMPLEMENTARIA */}
        {modalDetalleComplementaria && (
          <DetalleFacturaComplementaria
            open={modalDetalleComplementaria}
            onClose={() => {setModalDetalleComplementaria(false); setSelectedServicio(null)}}
            factura={selectedServicio!}
            facturasParciales={registros.filter(r => r.facturaRelacionada?._id === selectedServicio?._id)}
            pacientes={pacientes}
            onFacturaPagada={(f) => {
              setSelectedServicio(f);
              setRegistros(prev =>
                prev.map(r =>
                  r._id === f?._id
                    ? {
                        ...r,
                        estado: f?.estado ?? r.estado,
                        fechaPago: f?.fechaPago ?? null,
                      }
                    : r
                )
              );
              setAlert({ open: true, severity: "success", msg: "Factura pagada correctamente" });
            }}
            onFacturaEmitida={(f) => {
              setSelectedServicio(f);
              setRegistros(prev =>
                prev.map(r =>
                  r._id === f?._id
                    ? {
                        ...r,
                        estado: f?.estado ?? r.estado,
                        numero: f?.numero ?? r.numero,
                      }
                    : r.facturaRelacionada?._id === f?._id
                      ? {
                          ...r,
                          facturaRelacionada: {
                            ...r.facturaRelacionada,
                            estado: f?.estado ?? r.facturaRelacionada.estado,
                            numero: f?.numero ?? r.facturaRelacionada.numero,
                          },
                        }
                      : r
                )
              );
              setAlert({ open: true, severity: "success", msg: "Factura emitida correctamente" });
            }}
            onFacturaActualizada={({ facturaComplementaria }) => {
              const facturaReducida: IFactura = {
                _id: facturaComplementaria._id,
                numero: facturaComplementaria.numero,
                fechaFactura: facturaComplementaria.fechaFactura.toString(),
                fechaExpedicion: getFechaExpedicion(facturaComplementaria),
                tipo: facturaComplementaria.tipo,
                modalidad: "complementaria",
                servicio: facturaComplementaria.servicio,
                pacienteId: null,
                paciente: facturaComplementaria.receptor.nombre,
                servicioId: "",
                total: facturaComplementaria.importeTotal,
                estado: getEstadoFacturacion(facturaComplementaria.estado, facturaComplementaria.estadoPago),
                facturaRelacionada: undefined,
                fechaPago: facturaComplementaria.metodoPago?.fechaPago.toString(),
                entityId: facturaComplementaria.entityId ?? null,
              };
              setRegistros(prev =>
                prev.map(r =>
                  r._id === facturaReducida._id
                    ? facturaReducida
                    : r
                )
              );
              setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
            }}
            onFacturaRectificada={({ factura }) => {
              setRegistros(prev => [factura, ...prev]);
              setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
            }}
          />
        )}

        {/* DETALLE FACTURA BONO */}
        {modalDetalleFacturaBono && 
          <DetalleFacturaBono 
            open={modalDetalleFacturaBono}
            onClose={() => setModalDetalleFacturaBono(false)}
            facturaId={facturaId}
            onFacturaPagada={(f) => {
              setRegistros(prev =>
                prev.map(r =>
                  r._id === f?._id
                    ? {
                        ...r,
                        estado: f?.estado ?? r.estado,
                        fechaPago: f?.fechaPago ?? null,
                      }
                    : r
                )
              );
              setAlert({ open: true, severity: 'success', msg: 'Factura pagada correctamente' });
            }}
            onFacturaEmitida={(f) => {
              setRegistros(prev =>
                prev.map(r =>
                  r._id === f?._id
                    ? {
                        ...r,
                        estado: f?.estado ?? r.estado,
                        numero: f?.numero ?? r.numero,
                      }
                    : r
                )
              );
              setAlert({ open: true, severity: 'success', msg: 'Factura emitida correctamente' });
            }}
            onFacturaRectificada={(factura) => {
              const facturaReducida: IFactura = {
                _id: factura._id,
                numero: factura.numero,
                fechaFactura: factura?.fechaFactura?.toString()!,
                fechaExpedicion: getFechaExpedicion(factura),
                tipo: factura.tipo,
                modalidad: "rectificativa",
                servicio: factura.servicio,
                pacienteId: null,
                paciente: factura.receptor.nombre,
                servicioId: "",
                total: factura.importeTotal,
                estado: getEstadoFacturacion(factura.estado, factura.estadoPago),
                facturaRelacionada: undefined,
                fechaPago: factura.metodoPago?.fechaPago.toString(),
                entityId: factura.entityId ?? null,
              };
              setRegistros(prev => [facturaReducida, ...prev]);
              setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
            }}
          />
        }

        {/* DETALLE FACTURA CLASE */} 
        {modalDetalleFacturaClase &&
          <DetalleFacturaClase
            open={modalDetalleFacturaClase}
            onClose={() => setModalDetalleFacturaClase(false)}
            facturaId={facturaId}
            facturable={true}
            onEdit={(_pacienteId, _actividadId, factura, estado, _motivo) => {
              if (estado === "NO_FACTURABLE_PUNTUAL") {
                setRegistros(prev => prev.filter(r => r._id !== factura._id));
                return;
              }
              const updated = {
                _id: factura._id,
                estado: estado === "PAGADA" ? EstadoFacturacion.PAGADA : EstadoFacturacion.PENDIENTE_PAGO,
                servicio: factura.servicio,
                total: factura.importeTotal
              }
              setRegistros(prev =>
                prev.map(r => r._id === factura._id ? { ...r, ...updated } : r)
              );
            }}
            onFacturaPagada={(_pacienteId, _actividadId, id, fechaPago, estado, estadoPago) => {
              setRegistros(prev =>
                prev.map(r =>
                  r._id === id
                    ? {
                        ...r,
                        estado: getEstadoFacturacion(estado, estadoPago),
                        fechaPago: fechaPago ?? null,
                      }
                    : r
                )
              );
              setAlert({ open: true, severity: "success", msg: "Factura pagada correctamente" });
            }}
            onFacturaEmitida={(_pacienteId, _actividadId, id, estado, estadoPago, _periodo, numero) => {
              setRegistros(prev =>
                prev.map(r =>
                  r._id === id
                    ? {
                        ...r,
                        estado: getEstadoFacturacion(estado, estadoPago),
                        numero: numero ?? r.numero,
                      }
                    : r
                )
              );
              setAlert({ open: true, severity: "success", msg: "Factura emitida correctamente" });
            }}
            onFacturaRectificada={(_, __, factura) => {
              const facturaReducida: IFactura = {
                _id: factura._id,
                numero: factura.numero,
                fechaFactura: factura?.fechaFactura?.toString()!,
                fechaExpedicion: getFechaExpedicion(factura),
                tipo: factura.tipo,
                modalidad: "rectificativa",
                servicio: factura.servicio,
                pacienteId: null,
                paciente: factura.receptor.nombre,
                servicioId: "",
                total: factura.importeTotal,
                estado: getEstadoFacturacion(factura.estado, factura.estadoPago),
                facturaRelacionada: undefined,
                fechaPago: factura.metodoPago?.fechaPago.toString(),
                entityId: factura.entityId ?? null,
              };
              setRegistros(prev => [facturaReducida, ...prev]);
              setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
            }}
            onAnularFactura={(_pacienteId, _actividadId, _periodo, id, _motivo, anulacion) => {
              setRegistros(prev => aplicarAnulacion(prev, id, anulacion));
            }}
            onEntidadCambiada={(_pacienteId, _actividadId, _periodo, facturaAnuladaId, facturaNueva, anulacion) => {
              setRegistros(prev => aplicarCambioEntidad(prev, facturaAnuladaId, facturaNueva, anulacion));
              setAlert({ open: true, severity: "success", msg: "Entidad de facturación cambiada correctamente" });
            }}
            onEstadoCambiado={(_pacienteId, _actividadId, _periodo, factura) => {
              setRegistros(prev => prev.map(r => r._id === factura._id
                ? { ...r, estado: getEstadoFacturacion(factura.estado, factura.estadoPago), numero: factura.numero, fechaExpedicion: getFechaExpedicion(factura) }
                : r));
            }}
            onFacturaEliminada={(_pacienteId, _actividadId, _periodo, id) => {
              setRegistros(prev => prev.filter(r => r._id !== id));
              setAlert({ open: true, severity: "success", msg: "Factura eliminada correctamente" });
            }}
          />
        }

        {/* MODAL CREAR FACTURA MANUAL */}
        {modalCrearFacturaManual && 
          <CrearFacturaManualForm
            open={modalCrearFacturaManual}
            onClose={() => setModalCrearFacturaManual(false)}
            onSuccess={(factura) => {
              const facturaReducida: IFactura = {
                _id: factura._id,
                numero: factura.numero,
                fechaFactura: factura?.fechaFactura?.toString()!,
                fechaExpedicion: getFechaExpedicion(factura),
                tipo: factura.tipo,
                modalidad: "Total",
                servicio: factura.servicio,
                pacienteId: null,
                paciente: factura.receptor.nombre,
                servicioId: "",
                total: factura.importeTotal,
                estado: getEstadoFacturacion(factura.estado, factura.estadoPago),
                facturaRelacionada: undefined,
                fechaPago: factura.metodoPago?.fechaPago.toString(),
                entityId: factura.entityId ?? null,
              };
              setRegistros(prev => [facturaReducida, ...prev]);
              setFacturaId(factura._id);
              setModalDetalleFacturaManual(true);
              setAlert({ open: true, severity: "success", msg: "Factura creada correctamente" });
            }}
          />
        }

        {/* DETALLE FACTURA MANUAL */}
        {modalDetalleFacturaManual && 
          <DetalleFacturaManual 
            open={modalDetalleFacturaManual}
            onClose={() => setModalDetalleFacturaManual(false)}
            facturaId={facturaId}
            onEdit={(updatedFactura) => {
              const updated = {
                _id: updatedFactura._id,
                estado: getEstadoFacturacion(updatedFactura.estado, updatedFactura.estadoPago),
                servicio: updatedFactura.servicio,
                total: updatedFactura.importeTotal,
                fechaPago: updatedFactura.metodoPago?.fechaPago.toString() ?? null
              }
              setRegistros(prev =>
                prev.map(r => r._id === updatedFactura._id ? { ...r, ...updated } : r)
              );
            }}
            onFacturaPagada={(id, fechaPago, estado, estadoPago) => {
              setRegistros(prev =>
                prev.map(r =>
                  r._id === id
                    ? {
                        ...r,
                        estado: getEstadoFacturacion(estado, estadoPago),
                        fechaPago: fechaPago ?? null,
                      }
                    : r
                )
              );
              setAlert({ open: true, severity: "success", msg: "Factura pagada correctamente" });
            }}
            onFacturaEmitida={(id, estado, estadoPago, numero) => {
              setRegistros(prev =>
                prev.map(r =>
                  r._id === id
                    ? {
                        ...r,
                        estado: getEstadoFacturacion(estado, estadoPago),
                        numero: numero ?? r.numero,
                      }
                    : r
                )
              );
              setAlert({ open: true, severity: "success", msg: "Factura emitida correctamente" });
            }}
            onFacturaRectificada={(factura) => {
              const facturaReducida: IFactura = {
                _id: factura._id,
                numero: factura.numero,
                fechaFactura: factura?.fechaFactura?.toString()!,
                fechaExpedicion: getFechaExpedicion(factura),
                tipo: factura.tipo,
                modalidad: "rectificativa",
                servicio: factura.servicio,
                pacienteId: null,
                paciente: factura.receptor.nombre,
                servicioId: "",
                total: factura.importeTotal,
                estado: getEstadoFacturacion(factura.estado, factura.estadoPago),
                facturaRelacionada: undefined,
                fechaPago: factura.metodoPago?.fechaPago.toString(),
                entityId: factura.entityId ?? null,
              };
              setRegistros(prev => [facturaReducida, ...prev]);
              setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
            }}
            onAnularFactura={(id, _motivo, anulacion) => {
              setRegistros(prev => aplicarAnulacion(prev, id, anulacion));
            }}
            onEntidadCambiada={(facturaAnuladaId, facturaNueva, anulacion) => {
              setRegistros(prev => aplicarCambioEntidad(prev, facturaAnuladaId, facturaNueva, anulacion));
              setAlert({ open: true, severity: "success", msg: "Entidad de facturación cambiada correctamente" });
            }}
            onEstadoCambiado={(factura) => {
              setRegistros(prev => prev.map(r => r._id === factura._id
                ? { ...r, estado: getEstadoFacturacion(factura.estado, factura.estadoPago), numero: factura.numero, fechaExpedicion: getFechaExpedicion(factura) }
                : r));
            }}
            onFacturaEliminada={(id) => {
              setRegistros(prev => prev.filter(r => r._id !== id));
              setAlert({ open: true, severity: "success", msg: "Factura eliminada correctamente" });
            }}
          />
        }

        <Backdrop
          open={isDownloading}
          sx={{ 
            color: "#fff", 
            zIndex: (theme) => theme.zIndex.drawer + 1, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            textAlign: 'center',
            p: 2
          }}
        >
          {progresoDescarga === null ? (
            <CircularProgress color="inherit" />
          ) : (
            <Box sx={{ width: "min(360px, 80vw)" }}>
              <LinearProgress variant="determinate" value={progresoDescarga} color="inherit" sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          )}
          <Typography variant="h6" component="div">
            {progresoDescarga === null
              ? `Preparando ${facturasEnDescarga} ${facturasEnDescarga === 1 ? "factura" : "facturas"}…`
              : `Descargando facturas… ${progresoDescarga} %`}
          </Typography>
          <Typography variant="body2" component="div">
            No cierres esta pestaña hasta que termine.
          </Typography>
        </Backdrop>
    
        <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
          <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.msg}
          </Alert>
        </Snackbar>
    </>
    );
};

export default Facturas;